import { formatCurrencyToNumber } from "./helpers";

export type YesNo = "Sim" | "Não";

export type InstructionReportForm = {
  authorTestimony: YesNo | null;
  hadConfession: YesNo | null;
  confessionDescription: string;
  companyTestimony: YesNo | null;
  otherDefendantTestimony: YesNo | null;
  witnessTestimony: YesNo | null;
  witnessEvaluation: string;
  companyExclusionRequested: YesNo | null;
  observations: string;
};

export type ConciliationReportForm = {
  totalAmount: string;
  installments: string;
  paymentMethod: string;
  observations: string;
  maxCompanyProposal: string;
  minAcceptedProposal: string;
  hadCourtProposal: string | null;
  inHearingMinutes: string | null;
};

export function isInstructionReportValid(form: InstructionReportForm): boolean {
  const requiredFields: (keyof InstructionReportForm)[] = [
    "authorTestimony",
    "companyTestimony",
    "otherDefendantTestimony",
    "witnessTestimony",
  ];

  if (form.authorTestimony === "Sim") {
    requiredFields.push("hadConfession");
  }
  if (form.hadConfession === "Sim") {
    requiredFields.push("confessionDescription");
  }
  if (form.witnessTestimony === "Sim") {
    requiredFields.push("witnessEvaluation");
  }

  return requiredFields.every((field) => !!form[field]);
}

export function isConciliationReportValid(
  hadConciliation: string | null,
  form: Pick<
    ConciliationReportForm,
    | "totalAmount"
    | "installments"
    | "paymentMethod"
    | "hadCourtProposal"
    | "inHearingMinutes"
  >,
): boolean {
  if (!hadConciliation) {
    return false;
  }
  if (hadConciliation === "Sim") {
    return !!form.totalAmount && !!form.installments && !!form.paymentMethod;
  }
  return !!form.hadCourtProposal && !!form.inHearingMinutes;
}

export function isCombinedHearingReportValid(
  conciliationValid: boolean,
  instructionValid: boolean,
  lawyerFeedbackValid = true,
  includeLawyerFeedback = true,
): boolean {
  return (
    conciliationValid &&
    instructionValid &&
    (includeLawyerFeedback ? lawyerFeedbackValid : true)
  );
}

export function formatInstructionReport(data: InstructionReportForm) {
  return {
    wasThereAuthorsTestimonial: data.authorTestimony === "Sim",
    wasThereConfession: data.hadConfession === "Sim",
    confessionObservations: data.confessionDescription || "",
    wasThereCompanyOrAgentTestimonial: data.companyTestimony === "Sim",
    wasThereOtherTestimonials: data.otherDefendantTestimony === "Sim",
    wasThereWitnessTestimonial: data.witnessTestimony === "Sim",
    witnessTestimonialObservations: data.witnessEvaluation || "",
    wasCompanyExclusionRequested: data.companyExclusionRequested === "Sim",
    instructionObservations: data.observations || "",
  };
}

export function formatConciliationReport(
  data: ConciliationReportForm & { hadConciliation: string | null },
) {
  if (data.hadConciliation === "Sim") {
    return {
      agreement: true,
      agreementAmount: formatCurrencyToNumber(data.totalAmount),
      agreementInstallments: data.installments,
      agreementPaymentMethod: data.paymentMethod,
      agreementObservations: data.observations,
    };
  }
  return {
    agreement: false,
    wasAgreementProposed: data.hadCourtProposal === "Sim",
    maxCompanyProposal: formatCurrencyToNumber(data.maxCompanyProposal),
    minAcceptedProposal: formatCurrencyToNumber(data.minAcceptedProposal),
    notes: `
      Informações constam em ata: ${data.inHearingMinutes}.
      Outras observações: ${data.observations}
      `,
  };
}
